import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ShareIcon from '@mui/icons-material/Share';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RedeemIcon from '@mui/icons-material/Redeem';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useSelector } from 'react-redux';
import { apiServices } from '../../../../services/apiServices';
import { Configuration } from '../../../../assets/data/config';
import { QRCode } from 'react-qrcode-logo';
import html2canvas from 'html2canvas';
import qrLogo from '../../../../assets/images/logos/qrlogo.png';
import communityImage from '../../../../assets/images/community_img.jpg';
import communityChatImage from '../../../../assets/images/community.png';


const AppDashboard = ({ onMenuSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);
  
  const [totalUsers, setTotalUsers] = useState(0);
  const [displayedCount, setDisplayedCount] = useState(0);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [sharing, setSharing] = useState(false);
  const qrCodeRef = React.useRef(null);

  // State variables for invited users
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loadingInvitedUsers, setLoadingInvitedUsers] = useState(false);
  const [invitedUsersError, setInvitedUsersError] = useState(null);

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Get user initials helper function
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fetch registered users data using the same API as Referrals module
  const fetchRegisteredUsers = async () => {
    if (!user?.peacekeeper_id) {
      setError('Peacekeeper ID not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiServices.getAPI(`users/referred-by/${user.peacekeeper_id}`);
      if (response && response.status >= 200 && response.status < 300) {
        const data = response.data;
        console.log('AppDashboard API Response:', data);

        // Handle the specific API response format
        let usersArray = [];

        if (data && data.users && Array.isArray(data.users)) {
          // API returns { message, count, peacekeeperId, users: [...] }
          usersArray = data.users;
          setTotalUsers(data.count || 0);
        } else if (Array.isArray(data)) {
          // Fallback: direct array response
          usersArray = data;
          setTotalUsers(data.length);
        } else {
          console.warn('Unexpected API response format:', data);
          usersArray = [];
          setTotalUsers(0);
        }

        setRegisteredUsers(usersArray);
      } else {
        throw new Error(`HTTP ${response?.status}: ${response?.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching referred users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch referred users');
      setTotalUsers(0);
      setRegisteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch registered users data on component mount
  useEffect(() => {
    fetchRegisteredUsers();
  }, [user?.peacekeeper_id]);

  // Counter animation effect
  useEffect(() => {
    if (totalUsers === 0) {
      setDisplayedCount(0);
      return;
    }

    const duration = 800; // Animation duration in milliseconds (reduced for faster animation)
    const steps = 30; // Number of animation steps
    const increment = totalUsers / steps;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const nextValue = Math.min(Math.floor(increment * currentStep), totalUsers);
      setDisplayedCount(nextValue);
      
      if (currentStep >= steps || nextValue >= totalUsers) {
        setDisplayedCount(totalUsers);
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [totalUsers]);

  // Fetch invited users
  const fetchInvitedUsers = async () => {
    if (!user?.peacekeeper_id) {
      setInvitedUsersError('Peacekeeper ID not found');
      return;
    }

    setLoadingInvitedUsers(true);
    setInvitedUsersError(null);

    try {
      const response = await apiServices.getAPI(`invitations/${user.peacekeeper_id}`);
      if (response && response.status >= 200 && response.status < 300) {
        const data = response.data;
        console.log('Invited Users API Response:', data);

        // Handle the API response format
        let invitationsArray = [];

        if (data && data.invitations && Array.isArray(data.invitations)) {
          // API returns { message, count, peacekeeperId, invitations: [...] }
          invitationsArray = data.invitations;
        } else if (Array.isArray(data)) {
          // Fallback: direct array response
          invitationsArray = data;
        } else {
          console.warn('Unexpected invitations API response format:', data);
          invitationsArray = [];
        }

        setInvitedUsers(invitationsArray);
      } else {
        throw new Error(`HTTP ${response?.status}: ${response?.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching invited users:', err);
      setInvitedUsersError(err.message || 'Failed to fetch invited users');
    } finally {
      setLoadingInvitedUsers(false);
    }
  };

  // Fetch invited users on component mount
  useEffect(() => {
    fetchInvitedUsers();
  }, [user?.peacekeeper_id]);

  // Helper function to get invitation status color
  const getInvitationStatusColor = (status) => {
    if (!status) return 'default';

    const statusLower = status.toLowerCase();
    if (statusLower === 'accepted' || statusLower === 'completed' || statusLower === 'success') {
      return 'success';
    } else if (statusLower === 'pending' || statusLower === 'sent') {
      return 'warning';
    } else if (statusLower === 'rejected' || statusLower === 'failed') {
      return 'error';
    } else if (statusLower === 'expired') {
      return 'default';
    }
    return 'default';
  };

  // Generate referral link based on usertype and temp_peacekeeper_id (same logic as Certificate)
  const generateReferralLink = () => {
    if (!user) return '';

    const domain = Configuration.frontendDomain;
    const peacekeeperId = user.peacekeeper_id || 'IN-0000000-PK-IAPM';

    // Priority 1: For Entity usertype with categoryname, include category in URL
    // Format: domain/register?Name-Category-PeacekeeperID
    if (user.usertype === 'Entity' && user.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    // Format: domain/register?{temp_peacekeeper_id}
    if (user.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users (no entity, no temp ID)
    // Format: domain/register?{username}-{peacekeeper_id}
    return `${Configuration.frontendDomain}/register?${user?.username?.replace(/\s+/g, '-') || 'User'}-${user?.peacekeeper_id || 'IN-0000000-PK-IAPM'}`;
  };

  const handleShareReferral = () => {
    const link = generateReferralLink();
    if (link) {
      setReferralLink(link);
      setShowQRModal(true);
    } else {
      setError('Unable to generate referral link');
    }
  };

  const handleCloseQRModal = () => {
    setShowQRModal(false);
  };

  const handleDownloadQR = async () => {
    if (!qrCodeRef.current || !referralLink) return;
    
    setSharing(true);
    try {
      // Capture QR code as image
      const canvas = await html2canvas(qrCodeRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      
      canvas.toBlob((blob) => {
        if (!blob) {
          setError('Failed to generate QR code image');
          setSharing(false);
          return;
        }

        const filename = `QRCode-${user?.peacekeeper_id || 'referral'}.png`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSharing(false);
      }, 'image/png');
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setError('Failed to download QR code. Please try again.');
      setSharing(false);
    }
  };

  const handleShare = async () => {
    if (!qrCodeRef.current || !referralLink) return;
    
    setSharing(true);
    try {
      const shareText = `Join the I Am Peacekeeper Movement!\n\nScan the QR code or use this link:\n${referralLink}`;
      
      // Capture QR code as image
      const canvas = await html2canvas(qrCodeRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setError('Failed to generate QR code image');
          setSharing(false);
          return;
        }

        const filename = `QRCode-${user?.peacekeeper_id || 'referral'}.png`;
        const file = new File([blob], filename, { type: 'image/png' });

        // Try Web Share API with file
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Join I Am Peacekeeper Movement',
              text: shareText,
            });
            setShowQRModal(false);
            setSharing(false);
            return;
          } catch (shareError) {
            console.log('Web Share with file failed, trying text only:', shareError);
          }
        }

        // Try Web Share API with text only
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'Join I Am Peacekeeper Movement',
              text: shareText,
              url: referralLink,
            });
            setShowQRModal(false);
            setSharing(false);
            return;
          } catch (shareError) {
            console.log('Web Share failed:', shareError);
          }
        }

        // Fallback: Download QR code and copy link to clipboard
        // This helps with apps like Teams that may not support Web Share API
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        try {
          await navigator.clipboard.writeText(referralLink);
          alert('QR code downloaded! Referral link copied to clipboard. You can now attach the QR code image in Teams or any other app.');
          setShowQRModal(false);
        } catch (clipboardError) {
          console.error('Error copying to clipboard:', clipboardError);
          alert('QR code downloaded! Referral link: ' + referralLink);
          setShowQRModal(false);
        }
        setSharing(false);
      }, 'image/png');
    } catch (error) {
      console.error('Error sharing:', error);
      setError('Failed to share QR code. Please try again.');
      setSharing(false);
    }
  };

  const handleJoinGroupChat = () => {
    if (onMenuSelect) {
      onMenuSelect('Group Chat');
    }
  };

  const handleGoToCourses = () => {
    // TODO: Navigate to courses page
    console.log('Go to courses');
  };

  const handleRedeemRewards = (referredUser) => {
    // Same functionality as Referrals module
    console.log('Redeem rewards for:', referredUser);
    alert(`Redeem rewards functionality coming soon for ${referredUser.name || referredUser.username || 'User'}`);
  };

  // Loading state
  if (loading && registeredUsers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }} className='AppDashboardMainContainer'>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Top Blue Banner Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left: Community Banner */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              // background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
              color: 'white',
              p: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', justifyContent: 'center' }}>
              <Box
                component="img"
                src={communityImage}
                alt="Community"
                sx={{
                  width: { xs: '100%', sm: 220, md: 372 },
                  height: { xs: 'auto', sm: 220, md: 227 },
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
              
            </Box>
          </Card>
        </Grid>

        {/* Right: Total Registered Users */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
              color: 'white',
              p: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Total Registered Users
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              {displayedCount}
            </Typography>

            {/* Logged-in User Card */}
            {user && (
              <Card
                sx={{
                  mt: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                    {getInitials(user?.name || user?.username)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: 'white' }}>
                      {user?.name || user?.username || 'User'}
                    </Typography>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: 'white', opacity: 0.9 }}>
                      {user?.peacekeeper_id || 'N/A'}
                    </Typography>
                    {user?.created_at && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'white', opacity: 0.8 }}>
                        Registered on {formatDate(user.created_at)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Card>
            )}

            {/* Share Referral Button */}
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={handleShareReferral}
              sx={{
                mt: 1.5,
                backgroundColor: 'white',
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
                borderRadius: 2,
                textTransform: 'none',
                py: 1,
              }}
              fullWidth
            >
              Share your referral / QR code
            </Button>
          </Card>
        </Grid>
      </Grid>

      {/* Peacekeeper Community Chat Section */}
      <Card
        sx={{
          mb: 3,
          border: '2px solid #e3f2fd',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 32, color: '#1976d2' }} />
                <Typography variant="h5" fontWeight="bold">
                  I Am Peacekeeper Community Chat
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                While you wait for members to join under your referral, be part of the Peacekeeper Community Group Chat.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PeopleIcon />}
                onClick={handleJoinGroupChat}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                }}
              >
                Join Group Chat
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                component="img"
                src={communityChatImage}
                alt="Peacekeeper Community"
                sx={{
                  width: { xs: '100%', md: '57%' },
                  height: { xs: '80%', md: 168 },
                  objectFit: 'cover',
                  borderRadius: 2,
                  display: 'block',
                  marginLeft: { xs: 0, md: 12 },
                  mx: { xs: 'auto', md: 0 }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bottom Section */}
      <Grid container spacing={2}>
        {/* Registered Users Table */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Registered Users
                </Typography>
                {/* <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: 'none', color: '#1976d2' }}
                >
                  View All
                </Button> */}
              </Box>

              {loading && registeredUsers.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : registeredUsers.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'text.secondary',
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    No registered users yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share your code to get started.
                  </Typography>
                </Box>
              ) : (
                <>
                  {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>Date Registered</strong></TableCell>
                          <TableCell><strong>Action</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {registeredUsers.slice(0, 5).map((referredUser, index) => (
                          <TableRow key={referredUser.id || index}>
                            <TableCell>
                              {referredUser.name || referredUser.username || 'Unknown User'}
                            </TableCell>
                            <TableCell>
                              {formatDate(referredUser.created_at)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<RedeemIcon />}
                                  onClick={() => handleRedeemRewards(referredUser)}
                                  sx={{
                                    color: '#FF6B35',
                                    borderColor: '#FF6B35',
                                    textTransform: 'none',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 107, 53, 0.1)',
                                      borderColor: '#FF6B35'
                                    }
                                  }}
                                >
                                  Redeem
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Invited Users Table */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Invited Users
                </Typography>
              </Box>

              {loadingInvitedUsers && invitedUsers.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : invitedUsersError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {invitedUsersError}
                </Alert>
              ) : invitedUsers.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'text.secondary',
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    No invited users yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invite people to join the movement.
                  </Typography>
                </Box>
              ) : (
                <>
                  {loadingInvitedUsers && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Date Invited</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invitedUsers.slice(0, 5).map((invitedUser, index) => (
                          <TableRow key={invitedUser.id || invitedUser.email || index}>
                            <TableCell>
                              {invitedUser.email || invitedUser.emailid || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {formatDate(invitedUser.created_at)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={invitedUser.status || 'pending'}
                                size="small"
                                color={getInvitationStatusColor(invitedUser.status)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Courses and Activity Section */}
        <Grid size={{ xs: 12, md: 12 }}>
          {/* Courses Card */}
          <Card
            sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Chip
              label="NEW"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                backgroundColor: 'white',
                color: '#ff9800',
                fontWeight: 'bold',
                zIndex: 1,
              }}
            />
          </Card>

          
        </Grid>
      </Grid>

      {/* QR Code Preview Modal */}
      <Dialog
        open={showQRModal}
        onClose={handleCloseQRModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Share Referral / QR Code</Typography>
            <IconButton onClick={handleCloseQRModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
            {/* QR Code */}
            {referralLink && (
              <Box
                ref={qrCodeRef}
                sx={{
                  p: 2,
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  border: '2px solid #e0e0e0',
                  display: 'inline-block',
                }}
              >
                <QRCode
                  value={referralLink}
                  size={256}
                  logoImage={qrLogo}
                  logoWidth={40}
                  logoHeight={40}
                  logoOpacity={1}
                  logoPadding={3}
                  logoPaddingStyle="circle"
                  removeQrCodeTail={true}
                  qrStyle="squares"
                  eyeRadius={10}
                  fgColor="#214F7D"
                  eyeColor={["#214F7D", "#214F7D", "#214F7D"]}
                  backgroundOptions={{ color: "#ffffff" }}
                  logoOptions={{
                    backgroundColor: "#ffffff",
                    padding: 4,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: "#214F7D",
                  }}
                  ecLevel="H"
                />
              </Box>
            )}

            {/* Referral Link */}
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Referral Link:
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  wordBreak: 'break-all',
                }}
              >
                <Typography variant="body2">{referralLink}</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseQRModal} color="inherit">
            Close
          </Button>
          <Button
            onClick={handleDownloadQR}
            variant="outlined"
            startIcon={sharing ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={sharing}
            sx={{
              borderColor: '#214F7D',
              color: '#214F7D',
              borderRadius: '50px',
              '&:hover': {
                borderColor: '#214F7D',
                backgroundColor: 'rgba(33, 79, 125, 0.1)',
              },
            }}
          >
            {sharing ? 'Downloading...' : 'Download QR'}
          </Button>
          <Button
            onClick={handleShare}
            variant="contained"
            startIcon={sharing ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
            disabled={sharing}
            sx={{
              background: 'linear-gradient(to right, #214F7D, #3A8139)',
              color: 'white',
              borderRadius: '50px',
            }}
          >
            {sharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppDashboard;
